import React, { useState, useEffect } from 'react';
import { Users, Gem, Shield, BarChart3, Loader2, Pencil, Trash2, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Button from '../components/ui/Button';
import api from '../services/api';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [tokenCount, setTokenCount] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handleEditTokens = (user) => {
    setEditingUser(user);
    setTokenCount(user.tokens.toString());
    setError('');
    setSuccess('');
  };

  const handleUpdateTokens = async () => {
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${editingUser.id}/tokens`, {
        tokens: parseInt(tokenCount, 10),
      });
      setSuccess('Tokens atualizados com sucesso');
      setEditingUser(null);
      setTokenCount('');
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao atualizar tokens');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário ${userName}?`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setSuccess('Usuário excluído com sucesso');
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setTokenCount('');
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="flex h-64 items-center justify-center pt-20">
          <Loader2 className="h-10 w-10 animate-spin text-rose-500" />
        </div>
      </div>
    );
  }

  const statCards = stats
    ? [
        { label: 'Total de usuários', value: stats.totalUsers, Icon: Users, color: 'text-rose-400 bg-rose-500/15' },
        { label: 'Total de tokens', value: stats.totalTokens, Icon: Gem, color: 'text-emerald-400 bg-emerald-500/15' },
        { label: 'Administradores', value: stats.adminUsers, Icon: Shield, color: 'text-amber-400 bg-amber-500/15' },
        { label: 'Média de tokens', value: stats.averageTokensPerUser, Icon: BarChart3, color: 'text-sky-400 bg-sky-500/15' },
      ]
    : [];

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-6xl px-4 pb-10 pt-24">
        <h1 className="mb-6 text-2xl font-semibold text-white">Painel administrativo</h1>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
            {success}
          </div>
        )}

        {stats && (
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {statCards.map(({ label, value, Icon, color }) => (
              <div
                key={label}
                className="rounded-2xl border border-white/10 bg-zinc-900 p-5"
              >
                <div className="flex items-center gap-4">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400">{label}</p>
                    <p className="text-xl font-semibold text-white">{value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
          <div className="border-b border-white/10 px-5 py-4">
            <h3 className="text-base font-semibold text-white">Usuários</h3>
            <p className="mt-0.5 text-sm text-zinc-400">Gerencie usuários e tokens</p>
          </div>

          <ul className="divide-y divide-white/5">
            {users.map((user) => (
              <li key={user.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-600 text-sm font-semibold text-white">
                    {user.nome.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{user.nome}</p>
                    <p className="text-xs text-zinc-400">{user.email}</p>
                    <p className="text-xs text-zinc-500">
                      Criado em {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">{user.tokens} tokens</p>
                    <p className="text-xs text-zinc-400">
                      {user.role === 'admin' ? 'Administrador' : 'Usuário'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEditTokens(user)} size="sm" variant="secondary" icon={Pencil}>
                      Tokens
                    </Button>
                    {user.role !== 'admin' && (
                      <Button
                        onClick={() => handleDeleteUser(user.id, user.nome)}
                        size="sm"
                        variant="danger"
                        icon={Trash2}
                      >
                        Excluir
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-zinc-900 p-6 shadow-2xl">
              <button
                onClick={cancelEdit}
                className="absolute right-4 top-4 text-zinc-500 hover:text-zinc-300"
              >
                <X className="h-5 w-5" />
              </button>
              <h3 className="mb-4 text-lg font-semibold text-white">
                Editar tokens — {editingUser.nome}
              </h3>
              <label className="mb-1.5 block text-sm font-medium text-zinc-300">
                Número de tokens
              </label>
              <input
                type="number"
                min="0"
                value={tokenCount}
                onChange={(e) => setTokenCount(e.target.value)}
                className="mb-5 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2.5 text-white focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
              <div className="flex gap-3">
                <Button onClick={handleUpdateTokens} fullWidth>
                  Atualizar
                </Button>
                <Button onClick={cancelEdit} variant="secondary" fullWidth>
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
